import type { CSSProperties } from "react";

import { IObjectWithTypename } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Values } from "@/constants";
import { ObjectFieldDefinitionType } from "@/objects/@types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function NodeLabelCell({
  fieldDefinition,
  size,
  hasDelta,
  deltaMode,
  cellBackground,
  deltaTooltip,
  isRowDimmed,
  requiredState = null,
}: {
  fieldDefinition: ObjectFieldDefinitionType<IObjectWithTypename>;
  size: "sm" | "md";
  hasDelta: boolean;
  deltaMode: boolean;
  cellBackground?: string;
  deltaTooltip: React.ReactNode;
  isRowDimmed?: boolean;
  /**
   * A required field's asterisk: red while it is empty (``missing``), muted
   * once filled, and none on an optional field.
   */
  requiredState?: "missing" | "filled" | null;
}) {
  const resolvedCellBackground = cellBackground ?? "var(--card)";
  const rowHeight =
    size === "md" ? Values.DEFAULT_ROW_HEIGHT : Values.COMPACT_ROW_HEIGHT;
  const content = (
    <>
      {/* Delta indicator dot (when not in deltaMode) */}
      {hasDelta && !deltaMode && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-2 h-2 rounded-[50%] bg-foreground shrink-0 cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="left">{deltaTooltip}</TooltipContent>
        </Tooltip>
      )}
      <Typography
        level={size === "md" ? "body-sm" : "body-xs"}
        className={cn(
          hasDelta && !deltaMode ? "text-foreground" : "text-muted-foreground",
          "w-full min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap block",
          hasDelta && !deltaMode && "font-medium",
          deltaMode ? "pt-0" : "pt-1.5",
        )}
      >
        {fieldDefinition?.ReactIcon && (
          // Its icon on the label's line, as the words' start: the icon is
          // drawn as a block, so the span round it is a box of its own.
          <span
            className={cn(
              "inline-flex me-[0.375em]",
              hasDelta && !deltaMode && "text-foreground",
            )}
          >
            <Icon
              ReactIcon={fieldDefinition.ReactIcon}
              size="sm"
              style={{
                marginRight: "4px",
                transform: `scale(${size === "md" ? 1.1 : 0.9}) translateX(2px) translateY(2px)`,
              }}
            />
          </span>
        )}
        {fieldDefinition?.label}
        {requiredState && (
          <span
            aria-label="required"
            data-testid="required-asterisk"
            className={cn(
              "ml-0.5",
              requiredState === "missing"
                ? "text-destructive"
                : "text-muted-foreground/80",
            )}
          >
            *
          </span>
        )}
      </Typography>
    </>
  );

  return (
    <div
      className={cn(
        "relative flex min-w-0 overflow-hidden pb-0",
        deltaMode
          ? "items-center pt-0 bg-(--cell-background) min-h-(--node-row-height) h-(--node-row-height)"
          : // On its field's first line: a small row is shorter, its field's
            // line nearer its top.
            ["items-start", size === "sm" ? "pt-0" : "pt-1"],
        deltaMode
          ? size === "sm"
            ? "pl-1 pr-1"
            : "pl-2 pr-2"
          : size === "sm"
            ? "pl-1.5 pr-1.5"
            : "pl-2 pr-2",
      )}
      style={
        deltaMode
          ? ({
              "--cell-background": resolvedCellBackground,
              "--node-row-height": `${rowHeight}px`,
            } as CSSProperties)
          : undefined
      }
    >
      {deltaMode && isRowDimmed ? (
        <div
          data-dimmed-content
          className="opacity-[0.35] filter-[saturate(0)] min-w-0 flex-1 flex items-start gap-1"
        >
          {content}
        </div>
      ) : (
        content
      )}
    </div>
  );
}
